import { signOut } from 'aws-amplify/auth';
import type { IConfig } from '../Config';
import { COGNITO_CLIENT_OVERRIDE_STORAGE_KEY } from './cognitoClientOverride';

export const PENDING_ENTRA_LOGOUT_STORAGE_KEY = 'manage.pendingEntraLogout';

type LogoutConfig = Pick<IConfig, 'amplify' | 'featureFlags' | 'entraLogoutUrl'>;

export const getEntraLogoutUrl = (config: LogoutConfig): string => {
    if (!config.entraLogoutUrl) {
        throw new Error('ENTRA_LOGOUT_URL is required for Entra-only logout.');
    }

    const url = new URL(config.entraLogoutUrl);
    if (url.protocol !== 'https:') {
        throw new Error('ENTRA_LOGOUT_URL must use HTTPS.');
    }

    const returnUrl = config.amplify.Auth?.Cognito.loginWith?.oauth?.redirectSignOut
        .find(redirect => new URL(redirect).origin === window.location.origin);
    if (!returnUrl) {
        throw new Error('No Cognito sign-out return URL is configured for this origin.');
    }

    url.searchParams.set('post_logout_redirect_uri', returnUrl);
    return url.toString();
};

export const signOutWithEntra = async (config: LogoutConfig): Promise<void> => {
    if (config.featureFlags.usePartnerPortalDedicatedCognitoClient === false) {
        // Cognito returns here first; bootstrap completes Entra logout before rendering login.
        const logoutUrl = getEntraLogoutUrl(config);
        sessionStorage.setItem(PENDING_ENTRA_LOGOUT_STORAGE_KEY, logoutUrl);
    } else {
        sessionStorage.removeItem(PENDING_ENTRA_LOGOUT_STORAGE_KEY);
    }

    sessionStorage.removeItem(COGNITO_CLIENT_OVERRIDE_STORAGE_KEY);
    try {
        await signOut();
    } catch (error) {
        sessionStorage.removeItem(PENDING_ENTRA_LOGOUT_STORAGE_KEY);
        throw error;
    }
};

export const consumePendingEntraLogout = (config: LogoutConfig): string | undefined => {
    const pendingLogout = sessionStorage.getItem(PENDING_ENTRA_LOGOUT_STORAGE_KEY);
    sessionStorage.removeItem(PENDING_ENTRA_LOGOUT_STORAGE_KEY);

    if (pendingLogout && config.featureFlags.usePartnerPortalDedicatedCognitoClient === false) {
        return getEntraLogoutUrl(config);
    }
    return undefined;
};
